import { computed, inject, Injectable, signal } from '@angular/core';
import {
  InspectionPdfService,
  RepairTaskItemDto,
  RepairTaskService,
} from '@artemis-software/wr-api';
import { ListViewGroup } from '@shared/components/grouped-list-view/grouped-list-view.component';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { loader } from '@/utils/signals/loader';
import { openRepairTaskEditDialog } from '@components/dialog/repair-task-edit-dialog/repair-task-edit-dialog.component';
import { NbDialogService } from '@nebular/theme';
import { defaultPdfOptions } from '@/models/inspection-pdf-options';
import { repairTasksVisible } from '@/utils/repair-task-visibility';

@Injectable({
  providedIn: 'root',
})
export class InspectionContext {
  readonly repairTaskService = inject(RepairTaskService);
  readonly dialogService = inject(NbDialogService);
  private readonly inspectionPdfService = inject(InspectionPdfService);

  readonly notAssigned = 'Nicht zugeordnet';

  readonly repairTasksVisible = repairTasksVisible();

  readonly inspectionId = signal<string | undefined>(undefined);

  readonly loading = loader();

  readonly reloadRepairTasksSignal = signal(0);
  readonly reloadInspectionPdfsSignal = signal(0);

  readonly pdfTypes = new Set(defaultPdfOptions.map(option => option.type));

  readonly visiblePdfTypes = asyncComputed(() => {
    const id = this.inspectionId();
    if (!id) {
      return new Set<string>();
    }
    return this.inspectionPdfService.getVisiblePdfTypes(id);
  }, new Set<string>(), this.loading);

  readonly visiblePdfOptions = computed(() => {
    const visibleTypes = new Set(this.visiblePdfTypes());
    return defaultPdfOptions.filter(option => visibleTypes.has(option.type));
  });


  readonly repairTasks = asyncComputed(() => {
    this.reloadRepairTasksSignal();
    const id = this.inspectionId();
    if (!id || !this.repairTasksVisible()) {
      return [];
    }
    return this.repairTaskService.findAll({
      inspectionId: id,
      size: 10000,
    });
  }, [], this.loading);


  readonly inspectionPdfs = asyncComputed(() => {
    this.reloadInspectionPdfsSignal();
    const id = this.inspectionId();
    if (!id) {
      return [];
    }
    return this.inspectionPdfService.getLastInspectionPdfsByInspectionIdAndPdfTypes(id, this.pdfTypes);
  }, [], this.loading);


  groupRepairTasksByContractor(repairTasks: RepairTaskItemDto[]): ListViewGroup<RepairTaskItemDto>[] {
    const map = new Map<string, ListViewGroup<RepairTaskItemDto>>();
    for (const repairTask of repairTasks) {
      const contractor = repairTask.contractor;
      const key = contractor?.id ?? 'no-contractor';
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          displayName: contractor?.name ?? this.notAssigned,
          items: [],
        });
      }
      map.get(key)!.items.push(repairTask);
    }
    return Array.from(map.values()).sort((a: ListViewGroup<RepairTaskItemDto>, b: ListViewGroup<RepairTaskItemDto>) => {
      if (a.displayName === this.notAssigned) {
        return -1;
      }
      if (b.displayName === this.notAssigned) {
        return 1;
      }
      return a.displayName.localeCompare(b.displayName);
    });
  }

  getRepairTaskForEntry(inspectionEntryId: string) {
    return this.repairTasks().find(repairTask => repairTask.inspectionEntry?.id === inspectionEntryId);
  }

  hasRepairTask(inspectionEntryId: string) {
    return !!this.getRepairTaskForEntry(inspectionEntryId);
  }

  reloadRepairTasks() {
    this.reloadRepairTasksSignal.update(n => n + 1);
  }

  reloadInspectionPdfs() {
    this.reloadInspectionPdfsSignal.update(n => n + 1);
  }

  async editRepairTask(repairTask: RepairTaskItemDto) {
    await openRepairTaskEditDialog(this.dialogService, repairTask.id);
    this.reloadRepairTasks();
  }
}
